export const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD') // separate accents from characters
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-') // remove consecutive hyphens
    .replace(/^-+/, '') // remove leading hyphens
    .replace(/-+$/, ''); // remove trailing hyphens
};

export const generateGymSlug = (gym) => {
  if (!gym) return 'gym';

  const parts = [];

  if (gym.name || gym.gymName) {
    parts.push(gym.name || gym.gymName);
  }

  if (gym.city || gym.gymCity) {
    parts.push(gym.city || gym.gymCity);
  }

  if (gym.country || gym.gymCountry) {
    parts.push(gym.country || gym.gymCountry);
  }

  const rawString = parts.filter(Boolean).join(' ');
  return slugify(rawString) || 'gym';
};

export const generateEventSlug = (event) => {
  if (!event) return 'event';

  const parts = [];

  // 1. Instructor
  if (event.instructorName) {
    parts.push(event.instructorName);
  }

  // 2. Title
  if (event.title) {
    parts.push(event.title);
  }

  // 3. Location (City or Venue)
  if (event.city) {
    parts.push(event.city);
  } else if (event.address) {
    // Attempt to extract city from address (usually the last or second to last part)
    const addressParts = event.address.split(',').map((p) => p.trim());
    if (addressParts.length > 1) {
      // Very naive extraction, but better than nothing
      const potentialCity = addressParts[addressParts.length - 1].replace(/\d+/g, '').trim();
      if (potentialCity) parts.push(potentialCity);
    } else {
      parts.push(event.venueName || '');
    }
  } else if (event.venueName) {
    parts.push(event.venueName);
  }

  // 4. Year
  if (event.dateTime) {
    const rawDate = event.dateTime.toDate ? event.dateTime.toDate() : new Date(event.dateTime);
    if (!isNaN(rawDate)) {
      parts.push(rawDate.getFullYear().toString());
    }
  }

  // Combine, slugify, and deduplicate words
  const rawString = parts.filter(Boolean).join(' ');
  const rawSlug = slugify(rawString);

  // Deduplicate words (e.g. "craig-jones-craig-jones-seminar" -> "craig-jones-seminar")
  const slugWords = rawSlug.split('-');
  const uniqueWords = [...new Set(slugWords)];

  return uniqueWords.join('-') || 'event';
};

// Looks like a Firebase auto-id (20 chars, alphanumeric — case-sensitive).
export const looksLikeFirebaseId = (s) => typeof s === 'string' && /^[A-Za-z0-9]{20}$/.test(s);

// Reserved route segments that must never collide with a slug.
const RESERVED_SLUGS = new Set([
  'event',
  'events',
  'gym',
  'gyms',
  'profile',
  'login',
  'signup',
  'admin',
  'discover',
  'account',
  'my-bookings',
  'saved-events',
  'gym-dashboard',
  'gym-profile',
  'create-event',
  'edit-event',
  'booking',
  'stripe-connect',
  'support',
  'terms',
  'privacy',
  'impressum',
  'cookies',
  'seed',
]);

// Find a slug that doesn't already exist in `collectionName`. Appends -2, -3, …
// up to 50, then falls back to a timestamp suffix. Pass an extra in-memory
// `claimed` set when generating slugs in a batch (e.g. migration scripts).
export const findUniqueSlug = async (db, collectionName, baseSlug, options = {}) => {
  const { claimed, ignoreId } = options;
  const { collection, query, where, getDocs, limit } = await import('firebase/firestore');

  let candidate = baseSlug && !RESERVED_SLUGS.has(baseSlug) ? baseSlug : `${baseSlug}-1`;
  let suffix = 1;
  while (suffix <= 50) {
    if (!claimed?.has(candidate)) {
      const snap = await getDocs(
        query(collection(db, collectionName), where('slug', '==', candidate), limit(1))
      );
      const collides = snap.docs.some((d) => d.id !== ignoreId);
      if (!collides) {
        claimed?.add(candidate);
        return candidate;
      }
    }
    suffix += 1;
    candidate = `${baseSlug}-${suffix}`;
  }
  const fallback = `${baseSlug}-${Date.now()}`;
  claimed?.add(fallback);
  return fallback;
};

export const findUniqueEventSlug = (db, event, options) =>
  findUniqueSlug(db, 'events', generateEventSlug(event), options);

export const findUniqueGymSlug = (db, gym, options) =>
  findUniqueSlug(db, 'gyms', generateGymSlug(gym), options);
